import React from "react";
import { BadgeCheck, ExternalLink } from "lucide-react";
import { SOURCE_TYPE_META, fmtDate } from "@/components/ripeness/radarMeta";

/** Small pill naming a source type (News, PR, SEC, 10-K, Pipeline, CT.gov, CTIS). */
export function SourceTypeBadge({ type, short = true, testId }) {
    const m = SOURCE_TYPE_META[type] || { label: type || "source", short: type || "source", tone: "border-slate-600 text-slate-400" };
    return (
        <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] whitespace-nowrap ${m.tone}`}
            title={`${m.label}${m.verified ? " — verified evidence" : ""}`} data-testid={testId}>
            {m.verified ? <BadgeCheck className="w-2.5 h-2.5" /> : null}{short ? m.short : m.label}
        </span>
    );
}

const ROW_TYPES = ["sec_filing", "sec_10k", "press_release", "pipeline", "ctis"];

/** List-row badges for the extra sources behind a track's current score. */
export function TrackSourceBadges({ track }) {
    const c = track.source_counts || {};
    const types = ROW_TYPES.filter((t) => c[t]);
    if (!types.length) return null;
    return (
        <span className="inline-flex flex-wrap items-center gap-1" data-testid={`track-source-badges-${track.track_id}`}>
            {types.map((t) => (
                <span key={t} className="inline-flex items-center gap-0.5" data-testid={`track-source-badge-${t}-${track.track_id}`}>
                    <SourceTypeBadge type={t} />
                    {c[t] > 1 ? <span className="text-[10px] text-slate-500">{c[t]}</span> : null}
                </span>
            ))}
        </span>
    );
}

/** One evidence reference on a score step: type pill + id/date + link. */
export function EvidenceChip({ ev, testId }) {
    const label = ev.title || ev.id || "";
    const body = (
        <>
            <SourceTypeBadge type={ev.type} />
            <span className="truncate max-w-[260px]">{label}</span>
            {ev.date ? <span className="font-mono text-slate-500">{fmtDate(ev.date)}</span> : null}
            {ev.url ? <ExternalLink className="w-2.5 h-2.5 shrink-0" /> : null}
        </>
    );
    const cls = "inline-flex items-center gap-1.5 rounded border border-slate-700/80 bg-slate-900/60 px-1.5 py-0.5 text-[11px] text-slate-300 max-w-full";
    return ev.url ? (
        <a href={ev.url} target="_blank" rel="noreferrer" className={`${cls} hover:border-sky-400/50 hover:text-sky-200 transition-colors`} title={label} data-testid={testId}>
            {body}
        </a>
    ) : <span className={cls} title={label} data-testid={testId}>{body}</span>;
}
